import ClarityForm from '@/app/stores/[id]/storefront/components/tracking/ClarityForm';
import GTMForm from '@/app/stores/[id]/storefront/components/tracking/GTMForm';
import PixelForm from '@/app/stores/[id]/storefront/components/tracking/PixelForm';
import { TRACKING_CONFIGS, TRACKING_TYPE_PIXEL } from '@/app/stores/[id]/storefront/constants/tracking';
import {
  ClarityFormValues,
  GTMFormValues,
  PixelFormValues,
  TrackingFormValues,
  TrackingInfos,
  TrackingType,
  TrackingTypePixel,
} from '@/app/stores/[id]/storefront/types/tracking';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIntegrationsList } from '@/lib/api/integrations';
import { MAX_PIXEL_IDS_PER_PROVIDER, parsePixelIds } from '@/lib/tracking/pixel-id';
import { cn } from '@/lib/utils';
import { Copy, Info, Loader2, Target, Trash2 } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlowStep, StepVariant } from '../../types';
import { EditorSectionBlock } from '../EditorSectionBlock';

// ============================================================================
// Types & Schemas
// ============================================================================

interface TrackingConfigurationPanelProps {
  step?: FlowStep;
  config?: FlowStep['config'];
  isSplitStep?: boolean;
  selectedVariantIndex?: number;
  onVariantSelect?: (index: number) => void;
  onChange?: (step: FlowStep) => void;
  handleConfigChange?: (updates: Partial<any>) => void;
  storeId: string;
  /** Copy this step's pixel config to every other step (TAG-1213).
   * Returns false when the step has no pixels to copy. */
  onCopyPixelsToAllSteps?: () => boolean;
}

export function TrackingConfigurationPanel({
  step,
  config,
  isSplitStep = false,
  selectedVariantIndex = 0,
  onVariantSelect,
  onChange,
  handleConfigChange,
  storeId,
  onCopyPixelsToAllSteps,
}: TrackingConfigurationPanelProps) {
  const { t } = useTranslation('storefront');
  const { toast } = useToast();
  const [selectedPixel, setSelectedPixel] = React.useState<TrackingType>(TrackingType.FACEBOOK);
  const [selectedPixelIndex, setSelectedPixelIndex] = React.useState<number>(0);
  const trackingConfig = TRACKING_CONFIGS[selectedPixel];
  const isPixelType = TRACKING_TYPE_PIXEL.includes(selectedPixel);

  const { data: backendIntegrations, isLoading: isLoadingBackendIntegrations } = useIntegrationsList(storeId);

  const trackingDefaults = React.useMemo(() => {
    if (!backendIntegrations) return;

    // Build defaults as an array of TrackingFormValues per provider.
    const result: Partial<Record<TrackingType, TrackingFormValues[]>> = {};

    backendIntegrations
      .filter((integration) => integration.type === 'pixel' && integration.enabled)
      .forEach((integration) => {
        const provider = integration.provider as TrackingType;
        const trackingConfig = TRACKING_CONFIGS[provider];

        // Skip unknown providers
        if (!trackingConfig) return;

        if (!result[provider]) {
          result[provider] = [];
        }

        // ---------------------------------------------------------------------
        // Google Tag Manager – values come from `settings`
        // ---------------------------------------------------------------------
        if (provider === TrackingType.GTM) {
          const settings = (integration as any).settings || {};

          // If events are not present, default to all events defined in config as enabled.
          const eventsFromSettings = settings.events;
          const defaultEvents =
            trackingConfig.events?.reduce<Record<string, boolean>>((acc, evt) => {
              acc[evt.key as string] = true;
              return acc;
            }, {}) ?? {};

          const gtmDefaults: TrackingFormValues = {
            enabled: integration.enabled,
            containerId: settings.containerId ?? '',
            googleAdsConversionId: settings.googleAdsConversionId ?? undefined,
            googleAdsConversionLabel: settings.googleAdsConversionLabel ?? undefined,
            events: eventsFromSettings ?? defaultEvents,
          } as TrackingFormValues;

          result[provider] = [...(result[provider] || []), gtmDefaults];
          return;
        }

        // ---------------------------------------------------------------------
        // Microsoft Clarity – `settings.projectId`, not a pixel ID
        // ---------------------------------------------------------------------
        if (provider === TrackingType.CLARITY) {
          const settings = (integration as any).settings || {};
          const defaultEvents =
            trackingConfig.events?.reduce<Record<string, boolean>>((acc, evt) => {
              acc[evt.key as string] = true;
              return acc;
            }, {}) ?? {};

          result[provider] = [
            ...(result[provider] || []),
            {
              enabled: integration.enabled,
              projectId: settings.projectId ?? '',
              events: settings.events ?? defaultEvents,
            } as TrackingFormValues,
          ];
          return;
        }

        // ---------------------------------------------------------------------
        // Standard pixel-based providers (Facebook, TikTok, Snapchat)
        // ---------------------------------------------------------------------
        const rawPixelId = (integration as any).settings?.pixelId as
          | string
          | undefined;
        const events = (integration as any).settings?.events;

        // Support multiple pixel IDs coming from backend, separated by comma or
        // semicolon — but only after the value has been vetted (TAG-1871). A
        // pasted <script> snippet is full of commas: splitting it blindly seeded
        // 56 junk "pixels" per step, every one of which the storefront then fed
        // to ttq.load() and hung the checkout. parsePixelIds rejects such a
        // value whole rather than shredding it.
        const { ids: pixelIds, rejected, truncated } = parsePixelIds(rawPixelId);

        if (rejected.length > 0 || truncated) {
          console.warn(
            `[Tracking] Ignored ${rejected.length} malformed pixel ID(s) for provider "${provider}"` +
            `${truncated ? ` and capped the list at ${MAX_PIXEL_IDS_PER_PROVIDER}` : ''}.` +
            ' Fix the value in the store integration settings.',
            rejected,
          );
        }

        const pixelsForProvider: TrackingFormValues[] =
          pixelIds.length > 0
            ? pixelIds.map(
              (id: string): TrackingFormValues =>
              ({
                enabled: integration.enabled,
                pixelId: id,
                events,
              } as TrackingFormValues),
            )
            : [
              // Nothing usable — seed an EMPTY placeholder, never the raw value.
              // The `usableDefaults` filter below drops it, so a bad integration
              // setting no longer leaks into stepConfig.pixels at all.
              {
                enabled: integration.enabled,
                pixelId: '',
                events,
              } as TrackingFormValues,
            ];

        result[provider] = [...(result[provider] || []), ...pixelsForProvider];
      });

    return result;
  }, [backendIntegrations]);

  // Get the current config (variant config for split steps, main config for standard steps)
  const currentConfig = React.useMemo(() => {
    if (isSplitStep && step?.variants && step.variants[selectedVariantIndex]) {
      return step.variants[selectedVariantIndex]?.config;
    }
    return config;
  }, [isSplitStep, step, selectedVariantIndex, config]);

  const currentPixels = React.useMemo(() => {
    return currentConfig?.stepConfig?.pixels;
  }, [currentConfig]);

  const isLoading = isLoadingBackendIntegrations && !currentPixels;

  // Normalise any stored pixels into a PixelConfig[] for a given pixel type.
  const getPixelListFromStepConfig = React.useCallback(
    (pixelType: TrackingType): TrackingFormValues[] => {
      const pixels = currentPixels || {};
      const raw = pixels?.[pixelType];

      if (!raw) return [];

      // Already an array of PixelConfig
      if (Array.isArray(raw)) {
        return raw as TrackingFormValues[];
      }

      // Single PixelConfig (old format)
      if (typeof raw === 'object' && 'pixelId' in (raw)) {
        return [raw as TrackingFormValues];
      }

      // Fallback: if it's an object with multiple keys, take the values as PixelConfig[]
      if (typeof raw === 'object') {
        return Object.values(raw) as TrackingFormValues[];
      }

      return [];
    },
    [currentPixels],
  );

  // Get the list of pixels for the currently selected provider.
  const pixelsForSelectedProvider = React.useMemo(
    () => getPixelListFromStepConfig(selectedPixel),
    [getPixelListFromStepConfig, selectedPixel],
  );

  // Ensure selected index is always in bounds when the list changes.
  useEffect(() => {
    if (selectedPixelIndex >= pixelsForSelectedProvider.length) {
      setSelectedPixelIndex(0);
    }
  }, [pixelsForSelectedProvider.length, selectedPixelIndex]);



  // Initialise pixels in step config with defaults from backend integrations when empty.
  // Pre-strip entries that the save pipeline (useFunnelSave.cleanStepConfig) would
  // drop — entries with no usable pixelId/containerId. Without this, seeding empty
  // placeholders dirties the funnel, save wipes them, and the next render re-seeds,
  // permanently flagging the funnel as unsaved.
  useEffect(() => {
    if (currentPixels) return;
    if (!trackingDefaults) return;
    if (!step) return;

    const usableDefaults: Record<string, TrackingFormValues[]> = {};
    for (const [provider, entries] of Object.entries(trackingDefaults)) {
      if (!Array.isArray(entries)) continue;
      const filtered = (entries as TrackingFormValues[]).filter(
        (e: any) => e?.pixelId?.trim() || e?.containerId?.trim() || e?.projectId?.trim(),
      );
      if (filtered.length > 0) usableDefaults[provider] = filtered;
    }
    if (Object.keys(usableDefaults).length === 0) return;

    onChange?.({
      ...step,
      config: {
        ...config,
        stepConfig: {
          ...config?.stepConfig,
          pixels: usableDefaults,
        },
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on initial load, not on every step/config change
  }, [currentPixels, trackingDefaults]);

  // Helper to check if a pixel type is enabled
  const isPixelEnabled = (pixelType: TrackingType): boolean => {
    // If it's the currently selected pixel, check the form state
    // Otherwise check stepConfig.pixels
    const pixelList = getPixelListFromStepConfig(pixelType);
    // Check if any pixel is enabled or has a pixelId (indicates it's configured)
    return pixelList.some(
      (p) =>
        p.enabled ||
        ('pixelId' in p && !!p.pixelId) ||
        ('containerId' in p && !!p.containerId) ||
        ('projectId' in p && !!p.projectId),
    );
  };

  // Write a whole pixel list for the selected provider back into the step (or
  // variant) config. Shared by savePixelConfig and removePixelAtIndex so both
  // go through the same split-step / standard-step plumbing.
  const writePixelsForType = React.useCallback(
    (updatedPixelsForType: TrackingFormValues[]) => {
      // Store as array in stepConfig.pixels Record<string, PixelConfig[]>
      const pixels = {
        ...(currentConfig?.stepConfig?.pixels || {}),
        [selectedPixel]: updatedPixelsForType,
      };

      // Update stepConfig.pixels following the scripts pattern
      if (isSplitStep && step?.variants) {
        // SPLIT STEP: Update selected variant's stepConfig.pixels
        const selectedVariant = step.variants[selectedVariantIndex];
        const variantConfig = selectedVariant?.config;
        const updatedVariants: StepVariant[] = [...(step.variants || [])];
        updatedVariants[selectedVariantIndex] = {
          ...selectedVariant,
          config: {
            ...variantConfig,
            stepConfig: {
              ...variantConfig?.stepConfig,
              pixels,
            },
          },
        };
        onChange?.({ ...step, variants: updatedVariants });
      } else {
        // STANDARD STEP: Update main config
        if (handleConfigChange) {
          handleConfigChange({
            stepConfig: {
              ...config?.stepConfig,
              pixels,
            },
          });
        } else if (onChange && step) {
          onChange({
            ...step,
            config: {
              ...config,
              stepConfig: {
                ...config?.stepConfig,
                pixels,
              },
            },
          });
        }
      }
    },
    [
      selectedPixel,
      currentConfig,
      handleConfigChange,
      onChange,
      step,
      isSplitStep,
      selectedVariantIndex,
      config,
    ],
  );

  /**
   * Remove one pixel entry (TAG-1871). Without this the only way out of a
   * polluted config was to disable entries one at a time — the PX1 Research
   * incident left 56 junk entries on each of three steps, and editing "the"
   * pixel just overwrote entry[0] while the other 55 kept loading.
   */
  const removePixelAtIndex = React.useCallback(
    (index: number) => {
      const remaining = getPixelListFromStepConfig(selectedPixel).filter((_, i) => i !== index);
      writePixelsForType(remaining);
      setSelectedPixelIndex((current) => (current >= remaining.length ? Math.max(0, remaining.length - 1) : current));
    },
    [getPixelListFromStepConfig, selectedPixel, writePixelsForType],
  );

  // Save pixel configuration (similar to scripts - no explicit save button needed)
  const savePixelConfig = React.useCallback(
    (rawFormData: TrackingFormValues) => {

      // Trim the pixel/container/project ID so a copy-pasted value with
      // leading/trailing whitespace never lands in stepConfig.pixels (would
      // break tracking).
      const formData: TrackingFormValues =
        'projectId' in rawFormData && typeof rawFormData.projectId === 'string'
          ? { ...rawFormData, projectId: rawFormData.projectId.trim() }
          : 'containerId' in rawFormData && typeof rawFormData.containerId === 'string'
            ? {
              ...rawFormData,
              containerId: rawFormData.containerId.trim(),
              ...('googleAdsConversionId' in rawFormData && typeof rawFormData.googleAdsConversionId === 'string'
                ? { googleAdsConversionId: rawFormData.googleAdsConversionId.trim() }
                : {}),
              ...('googleAdsConversionLabel' in rawFormData && typeof rawFormData.googleAdsConversionLabel === 'string'
                ? { googleAdsConversionLabel: rawFormData.googleAdsConversionLabel.trim() }
                : {}),
            }
            : 'pixelId' in rawFormData && typeof rawFormData.pixelId === 'string'
              ? { ...rawFormData, pixelId: rawFormData.pixelId.trim() }
              : rawFormData;

      const existingPixelsForType = getPixelListFromStepConfig(selectedPixel);
      const updatedPixelsForType: TrackingFormValues[] = [...existingPixelsForType];

      if (updatedPixelsForType.length === 0) {
        updatedPixelsForType.push(formData);
      } else {
        updatedPixelsForType[selectedPixelIndex] = formData;
      }

      writePixelsForType(updatedPixelsForType);
    },
    [selectedPixel, selectedPixelIndex, getPixelListFromStepConfig, writePixelsForType],
  );

  // Handle variant selection for split steps
  const handleVariantSelect = (idx: number) => {
    if (onVariantSelect) {
      onVariantSelect(idx);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="ml-2 text-xs text-muted-foreground">{t('tracking.loadingPixels')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Per-step scope notice + copy-to-all-steps action (TAG-1213).
          Pixels are stored per step; merchants routinely assume they're
          funnel-wide and lose Purchase events on the thank-you step. */}
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-2">
          <p>{t('tracking.perStepNotice')}</p>
          {onCopyPixelsToAllSteps && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 bg-white text-xs dark:bg-transparent"
              onClick={() => {
                const copied = onCopyPixelsToAllSteps();
                toast(
                  copied
                    ? { description: t('stepEditor.pixelsCopiedToAllSteps') }
                    : { description: t('tracking.noPixelsToCopy'), variant: 'destructive' },
                );
              }}
            >
              <Copy className="h-3.5 w-3.5" />
              {t('tracking.copyToAllSteps')}
            </Button>
          )}
        </div>
      </div>

      {/* Pixel Type Selector */}
      <div className="space-y-2">
        <Label htmlFor="pixel-type">{t('tracking.pixelType')}</Label>
        <Select value={selectedPixel} onValueChange={(value) => setSelectedPixel(value as TrackingType)}>
          <SelectTrigger id="pixel-type" className="w-full">
            <SelectValue>
              <div className="flex items-center gap-2">
                <img src={trackingConfig.icon} alt={trackingConfig.alt} className="h-4 w-4" />
                <span>{trackingConfig.name}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(TRACKING_CONFIGS) as TrackingType[]).map((pixelType) => {
              const pixelConfig = TRACKING_CONFIGS[pixelType];
              const enabled = isPixelEnabled(pixelType);
              return (
                <SelectItem key={pixelType} value={pixelType} className="cursor-pointer">
                  <div className="flex items-center justify-between w-full gap-2">
                    <div className="flex items-center gap-2">
                      <img src={pixelConfig.icon} alt={pixelConfig.alt} className="h-4 w-4" />
                      <span>{pixelConfig.name}</span>
                    </div>
                    {enabled && (
                      <Badge variant="outline" className="shrink-0 bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800 text-xs">
                        {t('tracking.enabled')}
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Pixel ID selector (when multiple pixels exist for the selected type),
          plus a per-entry delete action (TAG-1871). */}
      {pixelsForSelectedProvider.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="pixel-id-selector">{t('tracking.pixelIdLabel')}</Label>
          <div className="flex items-center gap-2">
            {pixelsForSelectedProvider.length > 1 && (
              <Select
                value={String(selectedPixelIndex)}
                onValueChange={(value) => setSelectedPixelIndex(Number(value))}
              >
                <SelectTrigger id="pixel-id-selector" className="w-full">
                  <SelectValue placeholder={t('tracking.selectPixelId')} />
                </SelectTrigger>
                <SelectContent>
                  {pixelsForSelectedProvider.map((pixel, index) => {
                    const identifier =
                      'pixelId' in pixel
                        ? pixel.pixelId
                        : 'containerId' in pixel
                          ? pixel.containerId
                          : 'projectId' in pixel
                            ? pixel.projectId
                            : '';
                    // Index-based key: entries seeded from a bad paste can share
                    // an identifier (or have none), and a duplicate React key
                    // silently drops rows from the list.
                    return <SelectItem key={index} value={String(index)}>
                      {identifier || t('tracking.pixelIndex', { index: index + 1 })}
                    </SelectItem>
                  }
                  )}
                </SelectContent>
              </Select>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5 text-destructive hover:text-destructive"
              aria-label={t('tracking.removePixel')}
              onClick={() => {
                removePixelAtIndex(selectedPixelIndex);
                toast({ description: t('tracking.pixelRemoved') });
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('tracking.removePixel')}
            </Button>
          </div>
          {pixelsForSelectedProvider.length > 1 && (
            <p className="text-xs text-muted-foreground">
              {t('tracking.pixelCount', { total: pixelsForSelectedProvider.length })}
            </p>
          )}
        </div>
      )}
      {isPixelType && (
        <PixelForm
          type={selectedPixel as TrackingTypePixel}
          trackingConfig={trackingConfig as TrackingInfos<TrackingTypePixel>}
          isLoading={isLoading}
          savePixelConfig={savePixelConfig}
          defaultValues={
            (pixelsForSelectedProvider[selectedPixelIndex] as PixelFormValues | undefined) ?? {
              enabled: false,
              pixelId: '',
              events: {
                PageView: true,
                InitiateCheckout: true,
                Purchase: true,
              },
            }
          }
        />
      )}

      {selectedPixel === TrackingType.CLARITY && (
        <ClarityForm
          trackingConfig={trackingConfig as TrackingInfos<TrackingType.CLARITY>}
          isLoading={isLoading}
          savePixelConfig={savePixelConfig}
          defaultValues={
            (pixelsForSelectedProvider[selectedPixelIndex] as ClarityFormValues | undefined) ?? {
              enabled: false,
              projectId: '',
              events: {
                PageView: true,
                InitiateCheckout: true,
                Purchase: true,
                AddToCart: true,
                ViewContent: true,
              },
            }
          }
        />
      )}

      {selectedPixel === TrackingType.GTM && (
        <GTMForm
          trackingConfig={trackingConfig as TrackingInfos<TrackingType.GTM>}
          isLoading={isLoading}
          savePixelConfig={savePixelConfig}
          defaultValues={
            (pixelsForSelectedProvider[selectedPixelIndex] as GTMFormValues | undefined) ?? {
              enabled: false,
              containerId: '',
              events: {
                PageView: true,
                InitiateCheckout: true,
                Purchase: true,
                AddToCart: true,
                ViewContent: true,
              },
            }
          }
        />
      )}


      {/* Setup Instructions */}
      {trackingConfig.setupSteps && trackingConfig.setupSteps.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">{t('tracking.setupInstructions')}</h3>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              {trackingConfig.setupSteps.map((step: string, index: number) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      )}

    </div>
  );
}
